import { Injectable } from '@angular/core';
import { LoggerService } from './logger.service';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap, shareReplay } from 'rxjs/operators';
import { ApiService } from './api.service';
import { ApiResponse } from '../shared/types';
import { PublicCommonWebsiteContentApi } from '../campaign/campaign-editor/optional-setting/common-website-content.mapper';

export interface CampaignFilters {
  platform?: string;
  campaign_status?: string;
  location_id?: string;
  page?: number;
  limit?: number;
  search?: string;
}

export interface PublicCommonWebsiteLandingViewedPayload {
  tracking_token?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

export interface PublicCommonWebsiteLandingViewedResponse {
  tracking_token?: string;
}

/** Platform entry from GET campaign `platforms` (UTM + landing page). */
export interface CampaignPlatformEntry {
  platform: string;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  landing_page_url?: string | null;
}

export const CAMPAIGN_PLATFORM_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  google: 'Google Ads',
  email: 'Email',
  sms: 'Text Messaging',
  mailer: 'Physical Mailer',
  physical_mailer: 'Physical Mailer',
};

/** Normalizes `platforms` from string[] or API object[] to platform id strings. */
export function getCampaignPlatformIds(platforms: any): string[] {
  if (!Array.isArray(platforms)) {
    return [];
  }

  return platforms
    .map(item => {
      if (typeof item === 'string') {
        return item.trim();
      }
      if (item && typeof item === 'object' && 'platform' in item) {
        return String((item as CampaignPlatformEntry).platform).trim();
      }
      return '';
    })
    .filter(Boolean);
}

/** Builds POST/PUT `platforms` array for campaign create/update APIs. */
export function normalizeCampaignPlatformsForApi(platforms: any): CampaignPlatformEntry[] {
  if (!Array.isArray(platforms)) {
    return [];
  }

  return platforms
    .map(item => {
      if (typeof item === 'string') {
        const platform = item.trim();
        return platform ? { platform } : null;
      }
      if (item && typeof item === 'object') {
        const record = item as Record<string, any>;
        const platformValue = String(record['platform'] || record['id'] || '').trim();
        if (!platformValue) {
          return null;
        }
        const entry: CampaignPlatformEntry = { platform: platformValue };
        (['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const).forEach(
          key => {
            const value = record[key];
            if (value !== undefined && value !== null && String(value).trim() !== '') {
              entry[key] = String(value);
            }
          }
        );
        return entry;
      }
      return null;
    })
    .filter((item): item is CampaignPlatformEntry => item !== null);
}

/** Human-readable platform list for display (e.g. "Email, Text Messaging"). */
export function formatCampaignPlatformList(platforms: any): string {
  const ids = getCampaignPlatformIds(platforms);
  if (!ids.length) {
    return '';
  }
  return ids.map(id => CAMPAIGN_PLATFORM_LABELS[id] ?? id).join(', ');
}

export interface Campaign {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  campaign_status: string;
  current_step: number;
  status: boolean;
  offer_name: string;
  promo_code: string;
  offer_description: string;
  offer_type: string;
  max_discount: string;
  offer_discount: string;
  quantity_limit: number;
  usage_type: string;
  multi_use_limit: number | null;
  schedule_start_date: string;
  schedule_start_time: string;
  schedule_recurrence: string;
  schedule_end_date: string;
  schedule_end_time: string;
  created_at: string;
  updated_at: string;
  platforms: string[] | CampaignPlatformEntry[];
  segments: {
    id: string;
    name: string;
    customer_count?: number;
  }[];
  locations: {
    id: string;
    branch: string;
  }[];
  packages: {
    id: string;
    name: string;
  }[];
  contents: any;
  goals: {
    id: string;
    campaign_id: string;
    goal_name: string;
    event: string;
    target_value: number;
    timeframe: string;
    status: boolean;
    created_at: string;
    updated_at: string;
  }[];
  // Additional properties expected by child components
  location: string;
  location_list?: string[];
  user_segment: string;
  total_customers: number;
  start_date: string;
  performance: {
    impressions: string;
    clicks: string;
    conversions: string;
    revenue: string;
  };
  // Additional offer properties
  valid_until?: string;
  car_wash_type?: string;
  offer_wash_type?: string;
  /** GET campaign: true once the common_website landing page has a title or button text. */
  landing_page_configured?: boolean | null;
}

export interface CampaignTrackingCustomer {
  id: string | null;
  first_name: string;
  last_name: string;
  email: string;
  contact: string;
}

export interface CampaignTrackingEvent {
  id: string;
  campaign_id: string;
  customer_id: string | null;
  campaign_contact_id?: string | null;
  customer: CampaignTrackingCustomer | null;
  platform: string;
  event_type: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  url: string | null;
  metadata: Record<string, any> | null;
  created_at: string;
  event_time?: string | null;
  payment_time?: string | null;
  spent_amount: string;
  revenue_generated: string;
  campaign_clicked_at?: string | null;
  landing_page_viewed_at?: string | null;
  offer_received_at?: string | null;
  order_redeemed_at?: string | null;
}

export interface CampaignTrackingEventsFilters {
  page?: number;
  limit?: number;
  event_type?: string;
  platform?: string;
  start_date?: string;
  end_date?: string;
}

export interface CampaignTrackingEventsResponse {
  events: CampaignTrackingEvent[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    /** Leads with phone or email (excludes unknown / no-contact rows). */
    total_with_contact?: number;
  };
}

/** Tracking event types supported by the campaign tracking-events API. */
export const CAMPAIGN_TRACKING_EVENT_TYPES = [
  'opened',
  'clicked',
  'sent',
  'delivered',
  'impression',
  'conversion',
  'failed',
  'otp_send',
  'offer_received',
] as const;

export type CampaignTrackingEventType = (typeof CAMPAIGN_TRACKING_EVENT_TYPES)[number];

export function getCampaignTrackingEventTypesQueryParam(): string {
  return CAMPAIGN_TRACKING_EVENT_TYPES.join(',');
}

export interface CampaignResponse {
  campaigns: Campaign[];
  pagination: {
    page: string;
    limit: string;
    total: string;
  };
}

export interface CampaignStatistics {
  statistics: {
    total_campaigns: string;
    active_campaigns: string;
    scheduled_campaigns: string;
    completed_campaigns: string;
    total_sms_sent: string;
    total_emails_sent: string;
    total_conversions: string;
    total_opens: string;
    total_clicks: string;
    total_sent: string;
    conversion_rate: string;
    avg_open_rate: string;
    avg_click_rate: string;
    roi: number;
    spend: string;
    gain: string;
    /** Month-over-month spend comparison (strings per API). */
    this_month_spend?: string;
    prev_month_spend?: string;
    /** Month-over-month gain comparison. */
    this_month_gain?: number | string;
    prev_month_gain?: number | string;
  };
}

/**
 * CampaignService - Injectable service for managing campaign CRUD operations, publishing/archiving,
 * and providing cached campaign statistics and utility helpers for transforming campaign payloads.
 * @component
 * @selector app-campaign-service
 * @example
 *   <app-campaign-service [apiService]="apiService" (statistics$)="onStatistics($event)"></app-campaign-service>
 * @input ApiService apiService - Injected API service used to perform HTTP requests (GET, POST, PUT) for campaigns.
 * @output Observable<CampaignStatistics|null> statistics$ - Public observable stream that emits the latest cached campaign statistics (null when cleared or not yet fetched).
 *
 * Purpose:
 *   - Centralizes all server interactions related to campaigns (list, get, create, update, publish, archive).
 *   - Provides a cached, shared observable for campaign statistics to avoid duplicate network calls.
 *   - Supplies utility methods to build duplicate campaign payloads and to convert status codes to human-friendly labels.
 *
 * Key public methods:
 *   - getCampaigns(filters: CampaignFilters = {}): Observable<ApiResponse<CampaignResponse>>
 *       Fetch a paginated/filtered list of campaigns from the API. Supports platform, status, location, page, limit, and search.
 *   - getCampaign(campaignId: string): Observable<ApiResponse<Campaign>>
 *       Retrieve a single campaign by ID.
 *   - createCampaign(campaignData: any): Observable<ApiResponse<any>>
 *       Create a new campaign with the provided payload.
 *   - updateCampaign(campaignId: string, campaignData: any): Observable<ApiResponse<any>>
 *       Update an existing campaign.
 *   - archiveCampaign(campaignId: string, campaignName: string): Observable<ApiResponse<any>>
 *       Archive a campaign by setting its status to 'archived' and updating the name.
 *   - publishCampaign(campaignId: string): Observable<ApiResponse<any>>
 *       Trigger publishing of a campaign.
 *   - getCampaignStatistics(): Observable<ApiResponse<CampaignStatistics>>
 *       Fetch campaign statistics with in-memory caching and request deduplication. Uses a configurable cache duration (5 minutes by default).
 *   - getCurrentStatistics(): CampaignStatistics | null
 *       Synchronously return the last cached statistics value (or null if none).
 *   - clearStatisticsCache(): void
 *       Clear the statistics cache and reset related internal timers/state. Useful after campaign updates to force a refetch.
 *
 * Static utility methods:
 *   - buildDuplicatePayload(campaign: any): any
 *       Normalize and transform an existing campaign object into a payload suitable for duplicating a campaign.
 *       Handles transformations for platforms, segments, locations, contents (email, sms, facebook, google), goals, and excludes transient fields.
 *       Shared between list/table components and modals to ensure consistent duplication behavior.
 *   - getStatusDisplay(status: string): string
 *       Return a user-friendly label for an internal campaign status code (e.g., 'active' -> 'Active').
 *
 * Caching and concurrency behavior:
 *   - Uses a BehaviorSubject to hold the current statistics and exposes statistics$ for subscribers.
 *   - Implements request deduplication: while a statistics request is in-flight, subsequent callers receive the same observable.
 *   - Results are shareReplayed for multiple subscribers and stored for a short configurable duration (STATISTICS_CACHE_DURATION = 5 minutes).
 *   - The cache can be manually cleared with clearStatisticsCache() to force a fresh fetch.
 *
 * Important notes:
 *   - This service is providedIn: 'root' for application-wide singleton behavior.
 *   - No Angular lifecycle hooks are used (typical for services), but consumers should be aware of the in-memory caching semantics.
 */
@Injectable({
  providedIn: 'root',
})
export class CampaignService {
  // Cache for campaign statistics to prevent duplicate API calls
  private statisticsSubject = new BehaviorSubject<CampaignStatistics | null>(null);
  public statistics$ = this.statisticsSubject.asObservable();
  private statisticsCache$?: Observable<ApiResponse<CampaignStatistics>>;
  private lastStatisticsFetchTime = 0;
  private readonly STATISTICS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private isLoadingStatistics = false;

  constructor(
    private logger: LoggerService,
    private apiService: ApiService
  ) {}

  logError(message: string, error: any): void {
    this.logger.error(message, error);
  }

  /**
   * Fetches campaigns from the API using optional filters and returns an observable of the API response.
   * @example
   * etCampaigns({ platform: 'web', campaign_status: 'active', page: 1, limit: 20 })
   * Observable<ApiResponse<CampaignResponse>>
   * @param {{CampaignFilters}} {{filters}} - Optional filters (platform, campaign_status, location_id, page, limit, search).
   * @returns {{Observable<ApiResponse<CampaignResponse>>}} Observable that emits the ApiResponse containing CampaignResponse.
   **/
  getCampaigns(filters: CampaignFilters = {}): Observable<ApiResponse<CampaignResponse>> {
    const params: Record<string, any> = {};

    if (filters.platform) {
      params['platform'] = filters.platform;
    }
    if (filters.campaign_status) {
      params['campaign_status'] = filters.campaign_status;
    }
    if (filters.location_id) {
      params['location_id'] = filters.location_id;
    }
    if (filters.page) {
      params['page'] = filters.page;
    }
    if (filters.limit) {
      params['limit'] = filters.limit;
    }
    if (filters.search) {
      params['search'] = filters.search;
    }

    return this.apiService.get<CampaignResponse>('campaign', params);
  }

  /**
   * Get campaign statistics with caching to prevent duplicate API calls
   */
  getCampaignStatistics(): Observable<ApiResponse<CampaignStatistics>> {
    const now = Date.now();

    // Return cached data if still valid
    if (
      this.statisticsCache$ &&
      now - this.lastStatisticsFetchTime < this.STATISTICS_CACHE_DURATION
    ) {
      return this.statisticsCache$;
    }

    // If already loading, return the existing observable to prevent duplicate calls
    if (this.isLoadingStatistics && this.statisticsCache$) {
      return this.statisticsCache$;
    }

    // Mark as loading
    this.isLoadingStatistics = true;

    // Create new observable with caching
    this.statisticsCache$ = this.apiService.get<CampaignStatistics>('campaign/statistics').pipe(
      tap(response => {
        this.isLoadingStatistics = false;
        if (response.status && response.data) {
          this.statisticsSubject.next(response.data);
          this.lastStatisticsFetchTime = now;
        }
      }),
      shareReplay(1) // Cache the result for multiple subscribers
    );

    return this.statisticsCache$;
  }

  /**
   * Get current statistics value synchronously
   */
  getCurrentStatistics(): CampaignStatistics | null {
    return this.statisticsSubject.value;
  }

  /**
   * Clear statistics cache (useful when campaigns are updated)
   */
  clearStatisticsCache(): void {
    this.statisticsCache$ = undefined;
    this.lastStatisticsFetchTime = 0;
    this.statisticsSubject.next(null);
  }

  createCampaign(campaignData: any): Observable<ApiResponse<any>> {
    return this.apiService.post<any>('campaign', campaignData);
  }

  updateCampaign(campaignId: string, campaignData: any): Observable<ApiResponse<any>> {
    return this.apiService.put<any>(`campaign/${campaignId}`, campaignData);
  }

  getCampaign(campaignId: string): Observable<ApiResponse<Campaign>> {
    return this.apiService.get<Campaign>(`campaign/${campaignId}`);
  }

  /** Public common-website landing + success content (no auth). */
  getPublicCommonWebsiteContent(
    campaignId: string
  ): Observable<ApiResponse<PublicCommonWebsiteContentApi>> {
    return this.apiService.getWithoutAuth<PublicCommonWebsiteContentApi>(
      `campaign/public/common-website/${campaignId}`
    );
  }

  /** Submit landing page recipient to receive campaign offer (no auth). */
  sendPublicCommonWebsiteOffer(payload: {
    campaign_id: string;
    recipient: string;
    tracking_token?: string;
  }): Observable<ApiResponse<any>> {
    return this.apiService.postWithoutAuth(
      'campaign/public/common-website/customer/offers/send',
      payload
    );
  }

  /** Record public landing page view for campaign tracking (no auth). */
  trackPublicCommonWebsiteLandingViewed(
    campaignId: string,
    payload: PublicCommonWebsiteLandingViewedPayload
  ): Observable<ApiResponse<PublicCommonWebsiteLandingViewedResponse>> {
    return this.apiService.postWithoutAuth<PublicCommonWebsiteLandingViewedResponse>(
      `campaign/public/common-website/${campaignId}/tracking/landing-viewed`,
      payload
    );
  }

  getCampaignTrackingEvents(
    campaignId: string,
    filters: CampaignTrackingEventsFilters = {}
  ): Observable<ApiResponse<CampaignTrackingEventsResponse>> {
    return this.apiService.get<CampaignTrackingEventsResponse>(
      `campaign/${campaignId}/tracking-events`,
      this.buildTrackingEventsParams(filters)
    );
  }

  exportCampaignTrackingEvents(
    campaignId: string,
    filters: Omit<CampaignTrackingEventsFilters, 'page' | 'limit'> = {}
  ): Observable<Blob> {
    return this.apiService.getBlob(
      `campaign/${campaignId}/tracking-events/export`,
      this.buildTrackingEventsParams(filters)
    );
  }

  private buildTrackingEventsParams(
    filters: CampaignTrackingEventsFilters = {}
  ): Record<string, string | number> {
    const params: Record<string, string | number> = {};

    if (filters.page) {
      params['page'] = filters.page;
    }
    if (filters.limit) {
      params['limit'] = filters.limit;
    }
    if (filters.event_type) {
      params['event_type'] = filters.event_type;
    }
    if (filters.platform) {
      params['platform'] = filters.platform;
    }
    if (filters.start_date) {
      params['start_date'] = filters.start_date;
    }
    if (filters.end_date) {
      params['end_date'] = filters.end_date;
    }

    return params;
  }

  archiveCampaign(campaignId: string, campaignName: string): Observable<ApiResponse<any>> {
    const updatePayload = {
      campaign_status: 'archived',
      name: campaignName,
    };
    return this.apiService.put<any>(`campaign/${campaignId}`, updatePayload);
  }

  publishCampaign(campaignId: string): Observable<ApiResponse<any>> {
    return this.apiService.post<any>(`campaign/${campaignId}/publish`, {});
  }

  /**
   * Build the payload for duplicating a campaign.
   * Shared between CampaignTableComponent and ViewCampaignModalComponent.
   */
  static buildDuplicatePayload(campaign: any): any {
    const payload: any = {
      name: `${campaign.name} (Copy)`,
      campaign_status: 'draft',
      description: campaign.description || null,
      platforms: normalizeCampaignPlatformsForApi(campaign.platforms ?? campaign.platform ?? []),
      segment: campaign.segments || [],
      locations: campaign.locations || [],
      offer_name: campaign.offer_name,
      promo_code: campaign.promo_code,
      offer_description: campaign.offer_description,
      offer_type: campaign.offer_type || 'flat_discount',
      max_discount: campaign.max_discount,
      offer_discount: campaign.offer_discount,
      quantity_limit: campaign.quantity_limit,
      multi_use_limit: campaign.multi_use_limit,
      usage_type: campaign.usage_type || 'single',
      schedule_start_date: campaign.schedule_start_date,
      schedule_end_date: campaign.schedule_end_date,
      schedule_recurrence: campaign.schedule_recurrence || 'daily',
      offer_wash_type: campaign.offer_wash_type,
    };

    // Exclude schedule_start_time
    delete payload.schedule_start_time;

    // Transform segments to ID array
    if (Array.isArray(payload.segment)) {
      payload.segment = payload.segment.map((s: any) => (typeof s === 'object' ? s.id : s));
    }

    // Transform locations to ID array
    if (Array.isArray(payload.locations)) {
      payload.locations = payload.locations.map((l: any) => (typeof l === 'object' ? l.id : l));
    }

    // Transform contents from object/array to normalized array
    if (campaign.contents) {
      const contentsArray: any[] = Array.isArray(campaign.contents)
        ? [...campaign.contents]
        : [
            campaign.contents.email,
            campaign.contents.facebook,
            campaign.contents.sms,
            campaign.contents.sms_delivery,
            campaign.contents.mms,
            campaign.contents.mms_campaign,
            campaign.contents.rcs,
            campaign.contents.delivery_email,
            campaign.contents.email_delivery,
            campaign.contents.google,
            campaign.contents.common_website,
          ].filter(Boolean);

      payload.contents = contentsArray.map((content: any) => {
        const platform = content.platform || content.type;
        const processed: any = { platform };

        if (platform === 'sms_delivery' || (platform === 'sms' && content.sms_config)) {
          processed.platform = 'sms_delivery';
          if (content.sms_config !== undefined) {
            processed.sms_config = content.sms_config;
          }
        } else if (platform === 'sms') {
          if (content.sms_sender_contact !== undefined)
            processed.sms_sender_contact = content.sms_sender_contact;
          if (content.sms_body !== undefined) processed.sms_body = content.sms_body;
          if (content.sms_link !== undefined) processed.sms_link = content.sms_link;
        } else if (platform === 'mms') {
          if (content.status !== undefined) processed.status = content.status;
          if (content.mms_config !== undefined) processed.mms_config = content.mms_config;
        } else if (platform === 'mms_campaign') {
          if (content.sms_sender_contact !== undefined)
            processed.sms_sender_contact = content.sms_sender_contact;
          if (content.mms_config !== undefined) processed.mms_config = content.mms_config;
        } else if (platform === 'rcs') {
          if (content.rcs_config !== undefined) processed.rcs_config = content.rcs_config;
        } else if (
          platform === 'email_delivery' ||
          (platform === 'email' && content.email_config)
        ) {
          processed.platform = 'email_delivery';
          processed.status = content.status !== undefined ? content.status : true;
          processed.email_config = content.email_config;
        } else if (platform === 'email' || content.type === 'email') {
          processed.type = 'email';
          [
            'email_subject',
            'email_from_id',
            'email_header',
            'email_body_text',
            'email_body_text2',
            'email_body_text3',
            'email_body_text4',
            'email_button_text',
            'email_button_position',
            'email_button_color',
            'email_footer_name',
            'email_footer_street',
            'email_footer_city',
            'email_footer_state',
            'email_footer_zip',
            'email_reply_to',
            'email_footer_phone',
            'email_test_id',
          ].forEach(key => {
            if (content[key] !== undefined) processed[key] = content[key];
          });
          // Skip File objects for image fields
          ['email_logo_img', 'email_body_img'].forEach(key => {
            if (content[key] !== undefined && !(content[key] instanceof File))
              processed[key] = content[key];
          });
        } else if (platform === 'facebook') {
          ['fb_name', 'fb_caption', 'fb_headline', 'fb_button_text'].forEach(key => {
            if (content[key] !== undefined) processed[key] = content[key];
          });
          ['fb_logo_img', 'fb_img'].forEach(key => {
            if (content[key] !== undefined && !(content[key] instanceof File))
              processed[key] = content[key];
          });
        } else if (platform === 'google') {
          if (content.g_ad_title !== undefined) processed.g_ad_title = content.g_ad_title;
          if (content.g_ad_company !== undefined) processed.g_ad_company = content.g_ad_company;
          if (content.g_ad_img !== undefined && !(content.g_ad_img instanceof File))
            processed.g_ad_img = content.g_ad_img;
        } else if (platform === 'common_website') {
          if (content.landing_page) {
            processed.landing_page = content.landing_page;
          }
          if (content.success_page) {
            processed.success_page = content.success_page;
          }
        }
        return processed;
      });
    }

    // Transform goals
    if (campaign.goals && Array.isArray(campaign.goals)) {
      payload.goals = campaign.goals.map((goal: any) => {
        const processed: any = {
          goal_name: goal.goal_name,
          event: goal.event,
          target_value: parseInt(goal.target_value) || 0,
          timeframe: goal.timeframe,
          status: goal.status !== undefined ? goal.status : true,
        };
        if (goal.start_date) {
          const d = new Date(goal.start_date);
          if (!isNaN(d.getTime())) processed.start_date = d.toISOString().split('T')[0];
        }
        if (goal.end_date) {
          const d = new Date(goal.end_date);
          if (!isNaN(d.getTime())) processed.end_date = d.toISOString().split('T')[0];
        }
        return processed;
      });
    }

    return payload;
  }

  /**
   * Display-friendly label for a campaign status value.
   */
  static getStatusDisplay(status: string): string {
    const statusMap: Record<string, string> = {
      active: 'Active',
      scheduled: 'Scheduled',
      stopped: 'Stopped',
      completed: 'Completed',
      draft: 'Draft',
      archived: 'Archived',
      paused: 'Paused',
      resumed: 'Resumed',
    };
    return statusMap[status] || status;
  }
}

/**
 * Loads campaign statistics and invokes onSuccess with the data, then sets loading = false.
 * Shared by CampaignMetricsComponent and CampaignStatsComponent, which differ only
 * in their onSuccess callback (updateMetrics vs updateStats).
 */
export function loadCampaignStatistics(
  service: CampaignService,
  onSuccess: (stats: CampaignStatistics) => void,
  setLoading: (value: boolean) => void
): void {
  service.getCampaignStatistics().subscribe({
    next: response => {
      if (response.status && response.data) {
        onSuccess(response.data);
      }
      setLoading(false);
    },
    error: error => {
      service.logError('Error loading campaign statistics:', error);
      setLoading(false);
    },
  });
}
